import {Dispatch, SetStateAction, useCallback, useRef, useState} from 'react';

import {isDevelopment} from '../../environment/envHelpers';

/*
 * Types.
 */

interface UseMaybeControlledStateOptions<T> {
  /** The controlled value. If provided, the component is controlled. */
  controlledValue: T | undefined;
  /** The default value for uncontrolled mode. */
  defaultValue: T;
  /** Callback when the value changes. Called in both controlled and uncontrolled modes. */
  onChange?: (value: T) => void;
}

type UseMaybeControlledStateResult<T> = [
  /** The current value (controlled or internal). */
  value: T,
  /** Handler to update the value. Works in both controlled and uncontrolled modes. */
  setValue: Dispatch<SetStateAction<T>>,
];

/*
 * Hook.
 */

/**
 * A hook for managing state that can be either controlled or uncontrolled.
 *
 * This hook handles the complexity of supporting both controlled and uncontrolled
 * patterns for a single piece of state. It also warns in development if a component
 * switches from controlled to uncontrolled mode (or vice versa), which is typically
 * a bug.
 *
 * @example
 * // In a component that supports both controlled and uncontrolled selection:
 * const {value, setValue} = useMaybeControlledState({
 *   controlledValue: props.selection,
 *   defaultValue: {},
 *   name: 'selection',
 *   onChange: props.onSelectionChange,
 * });
 */
export function useMaybeControlledState<T>(
  options: UseMaybeControlledStateOptions<T>,
): UseMaybeControlledStateResult<T> {
  const {controlledValue, defaultValue, onChange} = options;

  const isControlled = controlledValue !== undefined;
  const isControlledRef = useRef(isControlled);

  // Warn if the component switches between controlled and uncontrolled.
  if (isDevelopment()) {
    if (isControlledRef.current !== isControlled) {
      const previousMode = isControlledRef.current ? 'controlled' : 'uncontrolled';
      const currentMode = isControlled ? 'controlled' : 'uncontrolled';

      // eslint-disable-next-line no-console
      console.error(
        `A component is changing from ${previousMode} to ${currentMode}. ` +
          `This is likely a bug. Decide between using a controlled or uncontrolled component ` +
          `for the lifetime of the component. More info: https://reactjs.org/link/controlled-components`,
      );
    }
  }

  const [internalValue, setInternalValue] = useState<T>(defaultValue);

  const value = isControlled ? controlledValue : internalValue;

  const setValue = useCallback(
    (newState: SetStateAction<T>) => {
      const newValue = isUpdaterFunction(newState) ? newState(value) : newState;

      if (!isControlled) {
        setInternalValue(newValue);
      }

      onChange?.(newValue);
    },
    [isControlled, value, onChange],
  );

  return [value, setValue];
}

function isUpdaterFunction<T>(u: SetStateAction<T>): u is (prev: T) => T {
  return typeof u === 'function';
}
